// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import HttpService from 'src/lib/HttpService';
import {stringify} from 'query-string';

const DEFAULT_DEVICES_URL = `api/ustrl/v2.0/devices`;
const DEVICES_ELIGIBILITY_URL = `claimEligibilities`;
const DEFAULT_CLAIM_DETAILS = `api/ustrl/v2.0/claimLimit`;

interface EligibilityLookUp {
    country: string;
    serialNumber: string;
    zipCode: string | null;
    agentTier: string;
}

export default {
    async getDevice(deviceId: string) {
        const response = await HttpService.proxiedApiCall({
            requestUrl: `${DEFAULT_DEVICES_URL}/${deviceId}`,
            type: 'get',
        });
        return response;
    },
    async getEligibility(data: EligibilityLookUp) {
        const queryParams = {
            agentTier: data.agentTier,
        };

        const response = await HttpService.proxiedApiCall({
            requestUrl: `${DEFAULT_DEVICES_URL}/${data.serialNumber}/${DEVICES_ELIGIBILITY_URL}?${stringify(queryParams)}`,
            type: 'get',
        });
        return response;
    },
    async getFullDeviceRepairOffer(data: any) {
        if (Object.keys(data).length === 0) {
            return {};
        }

        const queryParams = {
            agentTier: data.userAgent,
            countryCode: data.countryCode,
            postalCode: data.postalCode,
            symptomCode: data.symptomCode,
            soldToId: data.soldToId,
        };

        const response = await HttpService.proxiedApiCall({
            requestUrl: `${DEFAULT_DEVICES_URL}/${data.serialNumber}/offers/repair?${stringify(queryParams)}`,
            type: 'get',
        });
        return response;
    },
    async getSoftwareRepairOffer(data: any) {
        if (Object.keys(data).length === 0) {
            return {};
        }

        const queryParams = {
            agentTier: data.userAgent,
            countryCode: data.countryCode,
            postalCode: data.postalCode,
            symptomCode: data.symptomCode,
            soldToId: data.soldToId,
            isSoftwareOffer: data.isSoftwareOrder,
        };

        const response = await HttpService.proxiedApiCall({
            requestUrl: `${DEFAULT_DEVICES_URL}/${data.serialNumber}/offers/repair?${stringify(queryParams)}`,
            type: 'get',
        });
        return response;
    },
    async getAccessoryAndPartRepairOffer(data: any) {
        const {
            accessoriesPayload,
            additionalInfo,
        } = data;

        let { partsPayload } = data;

        if (!(accessoriesPayload.length || partsPayload.length)) {
            return {};
        }

        const queryParams = {
            agentTier: additionalInfo.userAgent,
            countryCode: additionalInfo.countryCode,
            postalCode: additionalInfo.postalCode,
            soldToId: additionalInfo.soldToId,
            hasParentOnDemandOrder: additionalInfo.hasParentOnDemandOrder === true
        };

        let requestURL = `${DEFAULT_DEVICES_URL}/offers/fulfillment?${stringify(queryParams)}`;
        const onDemandFruPartsPayload = partsPayload.filter((part:any) => part.OnDemandFru === true);
        if(onDemandFruPartsPayload.length > 0){
            requestURL = `${DEFAULT_DEVICES_URL}/offers/onDemand?${stringify(queryParams)}`;
            partsPayload = onDemandFruPartsPayload;
        } else {
            const commercialSparePartsPayload = partsPayload.filter((part:any) => part.IsCruOffer === true);
            if(commercialSparePartsPayload.length > 0){
                requestURL = `${DEFAULT_DEVICES_URL}/offers/cru?${stringify(queryParams)}`; //commercialOffers
                partsPayload = commercialSparePartsPayload;
            }
        }
        const response = await HttpService.proxiedApiCall({
            requestUrl: requestURL,
            type: 'post',
            body: [
                ...accessoriesPayload,
                ...partsPayload,
            ],
        });
        return response;
    },
    async getWarrantyClaimDetails(serialNumber: string) {
        const response = await HttpService.proxiedApiCall({
            requestUrl: `${DEFAULT_CLAIM_DETAILS}/${serialNumber}`,
            type: 'get',
        });
        return response;
    },
    async putDevice(deviceId: string) {
        const response = await HttpService.proxiedApiCall({
            requestUrl: `${DEFAULT_DEVICES_URL}/${deviceId}`,
            type: 'put',
        });
        return response;
    },
};
